import { useState } from 'react';
import RawHtml from 'utils/miscUtils/rawHtml';
import Accordion from 'components/Globals/Accordion/index';
import Tabs from 'components/Globals/Tabs';
import ContentTable, { type ContentTableProps } from './ContentTable';
import * as S from './ProductCardStyles';

interface AccordionItemContent {
  tables?: ContentTableProps[];
  html?: string;
  rates?: [{ value: string; text: string }];
  sectionIntro?: string;
}

export interface SectionValue {
  title: string;
  value: AccordionItemContent;
}

export interface ProductCardSectionProps {
  values: SectionValue[];
  handleSectionOpen: (value: boolean) => void;
}

const ProductCardSections = ({ values, handleSectionOpen }: ProductCardSectionProps) => {
  const [activeSection, setActiveSection] = useState<number | null>(null);

  const handleShowHide = (index: number) => {
    const accordionOpened = activeSection === index;
    handleSectionOpen(!accordionOpened);
    setActiveSection(accordionOpened ? null : index);
  };

  const tabsConfig = values.map((section, index) => ({
    children: (
      <section
        key={`sectionContent-${section.title}`}
        id={`sectionContent-${index}`}
        aria-labelledby={`sectionHeader-${index}`}
      >
        {section.value.sectionIntro && <div className='section-content-intro'>{section.value.sectionIntro}</div>}
        {RawHtml({ html: section.value.html })}
        {section.value.rates &&
          section.value.rates.map((rate) => (
            <S.RatesLine key={`${rate.value}-${index}`}>
              <div className='circle'>{rate.value}</div>
              <div className='rates-text'>{rate.text}</div>
            </S.RatesLine>
          ))}
        {section.value.tables &&
          section.value.tables.map((table) => (
            <ContentTable
              key={`sectionTable-${table.values}`}
              title={table.title}
              values={table.values}
              aria-labelledby={`sectionHeader-${index}`}
            />
          ))}
      </section>
    ),
    title: section.title,
  }));

  if (!values || values.length === 0) return null;

  return (
    <S.SectionContainer className='section-container'>
      <Tabs
        config={tabsConfig}
        defaultIndex={0}
        onClick={handleShowHide}
      />
      {values.map((section, index) => (
        <Accordion
          key={`${section.title}-${index}`}
          title={section.title}
          isExpanded={activeSection === index}
          onClick={() => handleShowHide(index)}
          index={index}
        >
          {section.value.sectionIntro && <div className='section-content-intro'>{section.value.sectionIntro}</div>}
          {RawHtml({ html: section.value.html })}
          {section.value.rates &&
            section.value.rates.map((rate) => (
              <S.RatesLine key={`${rate.value}-${index}`}>
                <div className='circle'>{rate.value}</div>
                <div className='rates-text'>{rate.text}</div>
              </S.RatesLine>
            ))}
          {section.value.tables &&
            section.value.tables.map((table) => (
              <ContentTable
                key={`${table.title}-${index}`}
                title={table.title}
                values={table.values}
              />
            ))}
        </Accordion>
      ))}
    </S.SectionContainer>
  );
};

export default ProductCardSections;
